import { nextTick, ref, watch } from 'vue';
import { Editor } from '@tiptap/vue-3';
import { createEditorExtensions } from '@/app/util/editor/editor';
import { mapActions } from '@/store/lib/vue3helpers.js';

export default function useEditorInit({ editor, editor_root, props, emit, closeLinkMenu }) {
  const { AUTOCOMPLETE_EMPLOYEES } = mapActions(['AUTOCOMPLETE_EMPLOYEES']);
  const employees_mentionable = ref(null);

  function handleMobileFocus() {
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        editor_root.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }

  function initializeEditor() {
    const editor_extensions = createEditorExtensions({
      placeholder: props.placeholder,
      with_mentions: props.with_mentions,
      fetch_mentionable_employees: fetchMentionableEmployees,
    });

    editor.value = new Editor({
      editable: !props.disabled,
      extensions: editor_extensions,
      content: props.content_html,
      onUpdate: ({ editor: editor_instance }) => {
        emit('update:content_html', editor_instance.getHTML());
      },
      onFocus: () => handleMobileFocus(),
    });

    if (props.focus_when_mounted) {
      nextTick(() => editor.value.commands.focus('start'));
    }
  }

  function destroyEditor() {
    if (!editor.value) return;
    editor.value.destroy();
    editor.value = null;
  }

  function clearContent() {
    editor.value.commands.clearContent(true);
  }

  async function fetchMentionableEmployees() {
    employees_mentionable.value ||= await AUTOCOMPLETE_EMPLOYEES({ project_id: props.project_id });
    return employees_mentionable.value;
  }

  watch(
    () => props.content_key,
    () => {
      editor.value.commands.setContent(props.content_html, { emitUpdate: false });
    },
  );

  watch(
    () => props.content_html,
    (content_html) => {
      if (editor.value && editor.value.getHTML() !== content_html) {
        editor.value.commands.setContent(content_html, { emitUpdate: false });
      }
    },
  );

  watch(
    () => props.signature_attached,
    () => {
      nextTick(() => {
        if (!editor.value) return;
        editor.value.commands.setContent(props.content_html, { emitUpdate: false });
        editor.value.commands.focus('start');
      });
    },
  );

  watch(
    () => props.disabled,
    (disabled) => {
      if (disabled) closeLinkMenu({ focusEditor: false });
      nextTick(() => editor.value.setEditable(!disabled));
    },
  );

  watch(
    () => props.type,
    () => {
      closeLinkMenu({ focusEditor: false });
      destroyEditor();
      nextTick(() => initializeEditor());
    },
  );

  return {
    clearContent,
    fetchMentionableEmployees,
    initializeEditor,
    destroyEditor,
  };
}
